// Waveform generators. Pure math — runs equally well on the main thread or in a Web Worker.
//
// ECG: sum-of-Gaussians per beat (PQRST), parameterised by HR + rhythm template.
// Capnography: square-with-ramps cycle, amplitude = etco2/40, period = ventilation cycle.
// Pleth: smoothed sinusoid with amplitude scaled by perfusion proxy.
// Defib artefact: brief saturation pulse on the ECG.
// CPR artefact: 1.66 Hz mechanical-oscillation overlay.

export type Rhythm =
  | 'sinus' | 'sinus_tach' | 'brady'
  | 'af' | 'aflutter' | 'svt'
  | 'vt_pulse' | 'vt_pulseless' | 'vf' | 'torsades'
  | 'asystole' | 'p_wave_asystole'
  | 'pea_narrow' | 'pea_broad'
  | 'av1'        // first-degree AV block (PR > 0.20s, every P → QRS)
  | 'chb'        // third-degree (complete) AV block — atrial + ventricular dissociation
  | 'idioventricular'   // slow, broad ventricular escape — post-ROSC or dying heart
  | 'agonal';    // slow, irregular, wide, terminal — RCUK p.92 "dying heart"

interface BeatTemplate {
  // Each entry: {amp (mV), centre (s into beat), width (s)}
  gaussians: Array<{ a: number; c: number; w: number }>;
}

// Sinus beat: P, Q, R, S, T (5 Gaussians)
const SINUS_BEAT: BeatTemplate = {
  gaussians: [
    { a: 0.12, c: 0.10, w: 0.02 },  // P
    { a: -0.10, c: 0.18, w: 0.005 }, // Q
    { a: 1.20, c: 0.20, w: 0.008 },  // R
    { a: -0.25, c: 0.22, w: 0.008 }, // S
    { a: 0.30, c: 0.36, w: 0.04 },   // T
  ],
};

// Wide-complex VT. Per LITFL: QRS ≥ 120 ms (often 160-200 ms), uniform monomorphic, T discordant
// to dominant deflection. Widened the Gaussians so on-screen QRS measures ≥ 120 ms at 25 mm/s.
const VT_BEAT: BeatTemplate = {
  gaussians: [
    { a: 1.40, c: 0.18, w: 0.035 }, // tall R, wide
    { a: -1.10, c: 0.26, w: 0.050 }, // deep wide S — gives the "tombstone" look
    { a: -0.30, c: 0.42, w: 0.06 }, // T DISCORDANT to dominant R deflection (per LITFL/Healio)
  ],
};

// AF: NO P wave, narrow QRS-T (like sinus QRS-T only, P amplitude removed).
// Irregularity comes from the per-beat timing logic; f-waves come from the post-template
// baseline overlay (suppressed during the QRS window).
const AF_BEAT: BeatTemplate = {
  gaussians: [
    { a: -0.08, c: 0.18, w: 0.005 }, // Q
    { a: 1.10, c: 0.20, w: 0.008 },  // R
    { a: -0.22, c: 0.22, w: 0.008 }, // S
    { a: 0.26, c: 0.36, w: 0.04 },   // T
  ],
};

// SVT: NO P wave (or buried "pseudo-S" in II/III/aVF — per LITFL). Narrow regular QRS at 140-280 bpm.
// QRS alternans (beat-to-beat amplitude variation) is a feature but visually too subtle to model.
const SVT_BEAT: BeatTemplate = {
  gaussians: [
    { a: -0.05, c: 0.24, w: 0.010 }, // pseudo-S retrograde-P notch (post-QRS, per LITFL)
    { a: -0.08, c: 0.18, w: 0.005 }, // Q
    { a: 1.05, c: 0.20, w: 0.008 },  // R
    { a: -0.20, c: 0.22, w: 0.008 }, // S
    { a: 0.32, c: 0.34, w: 0.035 },  // tall fused T (peaked at fast rates)
  ],
};

// PEA broad (slow + wide), low amplitude
const PEA_BROAD_BEAT: BeatTemplate = {
  gaussians: [
    { a: 0.30, c: 0.20, w: 0.03 },
    { a: -0.10, c: 0.28, w: 0.04 },
  ],
};

// PEA narrow — similar to sinus but no perfusion (handled by hemo, not waveform)
const PEA_NARROW_BEAT: BeatTemplate = SINUS_BEAT;

// First-degree AV block: P-QRS-T just like sinus, but the PR interval (P→R) is
// stretched beyond 0.20s. RCUK p.92: "If PR > 0.20s = first-degree AV block."
// We push the P-wave EARLIER in the beat (longer PR delay) while keeping QRS-T in
// the same place. Easier than re-parameterising the whole template.
const AV1_BEAT: BeatTemplate = {
  gaussians: [
    { a: 0.12, c: 0.02, w: 0.025 },  // P pushed back so PR ≈ 0.22-0.24s on screen
    { a: -0.10, c: 0.18, w: 0.005 }, // Q
    { a: 1.20, c: 0.20, w: 0.008 },  // R
    { a: -0.25, c: 0.22, w: 0.008 }, // S
    { a: 0.30, c: 0.36, w: 0.04 },   // T
  ],
};

// Idioventricular rhythm: slow ventricular escape, broad QRS, no P. RCUK p.93:
// "broad QRS complexes occurring slowly, often in dying patients" or post-ROSC.
// Similar to VT shape but at slow rate (driven by input HR, typically 20-40).
const IDIOVENTRICULAR_BEAT: BeatTemplate = {
  gaussians: [
    { a: 0.85, c: 0.20, w: 0.040 },  // wide R
    { a: -0.55, c: 0.30, w: 0.060 }, // wide S (broader than VT, lower-amplitude)
    { a: -0.25, c: 0.46, w: 0.07 },  // T discordant
  ],
};

function gaussian(t: number, a: number, c: number, w: number): number {
  const x = (t - c) / w;
  return a * Math.exp(-0.5 * x * x);
}

export interface SampleEcgArgs {
  /** sim time in ms */
  t_ms: number;
  rhythm: Rhythm;
  hr: number;                  // bpm, 0 in arrest
  cpr_active: boolean;
  cpr_quality_pct: number;
  shock_at_ms?: number;        // if set within 80ms, render saturation artefact
  rng_state?: number;          // for AF jitter / VF chaos
}

// Returns a single sample in mV in the range roughly [-1.5, 2]
export function sampleEcg({ t_ms, rhythm, hr, cpr_active, cpr_quality_pct, shock_at_ms, rng_state }: SampleEcgArgs): number {
  const t = t_ms / 1000;

  // Defib artefact: ~80ms saturation
  if (shock_at_ms !== undefined && t_ms - shock_at_ms >= 0 && t_ms - shock_at_ms < 80) {
    return 2.2 - ((t_ms - shock_at_ms) / 80) * 2;
  }

  let v = 0;
  switch (rhythm) {
    case 'asystole':
      // Real asystole isn't perfectly flat — slow baseline drift from ventilation, electrode
      // motion etc. Adds <0.05 mV wander so the trace looks like a real flat-line not a
      // disconnected lead. Confirmation in a second lead is still the real-world check.
      v = 0.03 * Math.sin(2 * Math.PI * 0.3 * t) + 0.02 * Math.sin(2 * Math.PI * 0.7 * t + 1.1);
      break;
    case 'vf':
      // VF spectral energy is concentrated 4-10 Hz in humans (Bonizzoni 2011 surface ECG;
      // ICD recordings ~ 4.7 Hz mean). Rebalanced toward the dominant band.
      // Two-stage envelope: a fast "wave packet" envelope (~3 Hz) gives the bursty look,
      // and a SLOW coarse-to-fine envelope (~0.08 Hz = ~12s period) makes the rhythm
      // visibly degrade in amplitude over time. RCUK p.91: "VF may be classified as
      // coarse or fine depending on amplitude" — we now span that range over a single run.
      {
        const coarse_fine = 0.45 + 0.45 * Math.sin(2 * Math.PI * 0.08 * t - 1.0); // 0.0..0.9 over ~12s
        const packet = 0.7 + 0.4 * Math.sin(2 * Math.PI * 0.27 * t + 1.3);
        v = coarse_fine * packet * (
          0.40 * Math.sin(2 * Math.PI * 5.3 * t)
          + 0.35 * Math.sin(2 * Math.PI * 7.1 * t + 1.1)
          + 0.28 * Math.sin(2 * Math.PI * 9.4 * t + 2.4)
          + 0.20 * Math.sin(2 * Math.PI * 11.7 * t + 0.7)
          + 0.10 * Math.sin(2 * Math.PI * 14.9 * t + 3.0)
        );
      }
      break;
    case 'p_wave_asystole':
      // RCUK p.91 "P-wave asystole — ventricular standstill with continuing P waves".
      // Treatment-wise non-shockable but pacing can sometimes salvage. Render = P waves
      // at sinus rate, NO QRS. Looks like sinus with the QRS-T missing.
      {
        const period = 60 / 75; // 75 bpm atrial
        const phase = (t % period) / period;
        v = gaussian(phase * 0.6, 0.15, 0.10, 0.022);
        v += 0.02 * Math.sin(2 * Math.PI * 0.3 * t); // mild baseline wander
      }
      break;
    case 'agonal':
      // RCUK p.92-93: "slow, irregular, wide ventricular complexes, often of varying
      // morphology … become slower and progressively broader before all recognisable
      // activity is lost". Modelled as irregular broad complexes ~0.2-0.4 Hz (12-25 bpm)
      // with random width and amplitude jitter.
      {
        const beatRate = 0.3 + 0.15 * Math.sin(2 * Math.PI * 0.05 * t); // 0.15-0.45 Hz
        const phaseFloat = (t * beatRate) % 1;
        const jitter = Math.sin(t * 11.7) * 0.5 + 0.5; // 0..1
        const amplitude = 0.4 + 0.5 * jitter;
        const width = 0.06 + 0.05 * jitter;
        v = amplitude * Math.exp(-Math.pow((phaseFloat - 0.15) / width, 2));
        v -= amplitude * 0.5 * Math.exp(-Math.pow((phaseFloat - 0.3) / (width * 1.2), 2));
      }
      break;
    case 'torsades': {
      // Polymorphic VT at ~220 bpm with axis rotating 180° every ~10-12 beats →
      // full twist period ~2.7-3.2 s → envelope ~0.35 Hz. Deeper envelope (0.6 + 0.8) so
      // the trace visibly "almost flatlines" between twists (clinical hallmark).
      const envelope = 0.6 + 0.8 * Math.sin(2 * Math.PI * 0.35 * t);
      v = envelope * (
        0.55 * Math.sin(2 * Math.PI * 6.5 * t) +
        0.40 * Math.sin(2 * Math.PI * 9.1 * t + 1.3) +
        0.25 * Math.sin(2 * Math.PI * 12.3 * t + 0.6)
      );
      break;
    }
    case 'chb': {
      // Complete heart block: P waves and QRS-T complexes are INDEPENDENT (atrioventricular
      // dissociation). Per LITFL: atrial rate at sinus rate (~75/min), ventricular escape
      // either junctional (40-60/min, narrow QRS) or ventricular (20-40/min, wide QRS).
      // We use ventricular escape (broad QRS, slow). Sum the two streams.
      const ATRIAL_HZ = 75 / 60;     // 75 bpm Ps
      const VENT_HZ = (hr > 0 ? hr : 32) / 60; // ventricular escape rate from input
      const atrialPeriod = 1 / ATRIAL_HZ;
      const ventPeriod = 1 / VENT_HZ;
      // P wave train
      const pPhase = (t % atrialPeriod) / atrialPeriod;
      v += gaussian(pPhase * 0.6, 0.15, 0.10, 0.022);
      // Wide ventricular escape QRS-T train
      const vPhase = (t % ventPeriod) / ventPeriod;
      const tv = vPhase * 0.6;
      v += gaussian(tv, 1.10, 0.18, 0.035);
      v += gaussian(tv, -0.45, 0.26, 0.045);
      v += gaussian(tv, -0.25, 0.42, 0.06);
      break;
    }
    case 'aflutter': {
      // RCUK p.95: "F waves at ~300/min, sawtooth in II/III/aVF. AV conduction ratio
      // determines ventricular rate. 2:1 → ~150 bpm; 3:1 → 100; 4:1 → 75; 1:1 → 300
      // (rare, WPW)." Render = sawtooth F-wave train + QRS-T at every N-th flutter
      // wave (N = 60×300/hr ≈ conduction ratio). hr=0 means we pick 150 (2:1 default).
      const ventHr = hr > 0 ? hr : 150;
      const flutterHz = 5;             // 300/min
      const conductionRatio = Math.max(1, Math.round(300 / ventHr));
      const flutterPeriod = 1 / flutterHz;
      const ventPeriod = flutterPeriod * conductionRatio;
      // Sawtooth F waves (negative-going in inferior leads — classic Atrial flutter look)
      const fPhase = (t % flutterPeriod) / flutterPeriod;
      // Sawtooth: linear ramp from -0.18 to +0.05 over the period, instantaneous drop
      const saw = fPhase < 0.95 ? (-0.18 + 0.23 * (fPhase / 0.95)) : (0.05 - 1.20 * ((fPhase - 0.95) / 0.05));
      v = saw;
      // QRS-T overlaid at each conducted beat
      const vPhase = (t % ventPeriod) / ventPeriod;
      const tInBeat = vPhase * 0.6;
      // Use narrow QRS-T (no P — flutter has F waves instead)
      v += gaussian(tInBeat, -0.10, 0.18, 0.005);  // Q
      v += gaussian(tInBeat, 1.10, 0.20, 0.008);   // R
      v += gaussian(tInBeat, -0.22, 0.22, 0.008);  // S
      v += gaussian(tInBeat, 0.26, 0.36, 0.04);    // T
      break;
    }
    case 'sinus':
    case 'sinus_tach':
    case 'brady':
    case 'pea_narrow':
    case 'vt_pulseless':
    case 'vt_pulse':
    case 'pea_broad':
    case 'af':
    case 'svt':
    case 'av1':
    case 'idioventricular': {
      const effectiveHr = hr > 0 ? hr :
        rhythm === 'vt_pulseless' ? 180 :
        rhythm === 'svt' ? 170 :
        rhythm === 'idioventricular' ? 30 :
        60;
      const beatPeriod = 60 / effectiveHr; // seconds
      // AF: properly "irregularly irregular" — beat-by-beat jitter changes the
      // effective period for THIS beat, not just the phase within a fixed grid.
      // We compute beat boundaries cumulatively using a deterministic pseudo-random offset
      // per beat (≤30% of the base period).
      let phase: number;
      if (rhythm === 'af') {
        let cum = 0;
        let i = 0;
        while (cum < t && i < 4000) {
          const r = Math.sin(i * 9.13 + 2.7) * 0.5 + 0.5; // 0..1
          const thisPeriod = beatPeriod * (0.7 + 0.6 * r); // 0.7..1.3x
          if (cum + thisPeriod > t) {
            phase = (t - cum) / thisPeriod;
            break;
          }
          cum += thisPeriod;
          i++;
        }
        phase = phase! ?? 0;
      } else {
        phase = (t % beatPeriod) / beatPeriod;
      }
      const tInBeat = phase * 0.6; // collapse to 0.6s window for template
      const template =
        rhythm === 'vt_pulse' || rhythm === 'vt_pulseless' ? VT_BEAT :
        rhythm === 'pea_broad' ? PEA_BROAD_BEAT :
        rhythm === 'pea_narrow' ? PEA_NARROW_BEAT :
        rhythm === 'af' ? AF_BEAT :
        rhythm === 'svt' ? SVT_BEAT :
        rhythm === 'av1' ? AV1_BEAT :
        rhythm === 'idioventricular' ? IDIOVENTRICULAR_BEAT :
        SINUS_BEAT;
      for (const g of template.gaussians) v += gaussian(tInBeat, g.a, g.c, g.w);
      // AF: superimpose f-wave baseline activity. Per AHA/LITFL: coarse f-waves > 0.1 mV,
      // fine < 0.1 mV. Frequency 240-420/min (4-7 Hz). Add a 3rd higher-frequency sine
      // to break up periodicity.
      if (rhythm === 'af') {
        const onR = tInBeat > 0.16 && tInBeat < 0.28; // suppress wavelets over the QRS
        if (!onR) {
          v += 0.06 * Math.sin(2 * Math.PI * 5.7 * t + 1.1)
             + 0.04 * Math.sin(2 * Math.PI * 8.3 * t + 0.4)
             + 0.03 * Math.sin(2 * Math.PI * 12.1 * t + 2.7);
        }
      }
      // Sinus tach at high rates: T-P fusion creates "camel hump" look. Already happens
      // naturally because the T-wave tail extends into the next P at fast rates.
      break;
    }
  }

  // CPR mechanical artefact (1.66 Hz = 100 cpm)
  if (cpr_active && cpr_quality_pct > 0) {
    const amp = 0.3 + 0.4 * (cpr_quality_pct / 100);
    v += amp * Math.sin(2 * Math.PI * 1.66 * t);
  }

  // Mild high-freq noise so it looks like a real trace
  if (rng_state !== undefined) {
    // deterministic noise based on t and seed
    v += 0.02 * (Math.sin(t * 31.7 + rng_state) + Math.sin(t * 53.1 + rng_state * 1.7));
  } else {
    v += 0.015 * Math.sin(t * 47.3);
  }

  return v;
}

// Capnography phase morphology (RCUK Ch 7 — Airway management):
//   Phase 0 (inspiration) — flat at 0 mmHg
//   Phase 1 (expiration begins, dead-space gas) — still flat at 0
//   Phase 2 (expiratory upstroke) — rapid rise as alveolar gas reaches the sensor
//   Phase 3 (alveolar plateau) — slow rise; the value at end-expiration is the EtCO₂
//   Phase 0 again on next inspiration — sharp downstroke to 0
//
// Special patterns:
//   • Oesophageal intubation — flatline near 0 even with apparent ventilation
//   • Hypoventilation — slow rate, larger plateau slope (CO₂ accumulates)
//   • ROSC spike — sudden ≥10 mmHg rise from baseline (high SE+SP for ROSC)
//   • Shark-fin (obstructive disease / bronchospasm) — slow phase-2 rise, no flat plateau
//   • CPR — small irregular wavelets from compressions
export interface SampleCapnoArgs {
  t_ms: number;
  etco2_mmhg: number;
  rr_bpm: number;
  intubated: boolean;
  oesophageal?: boolean;
  bronchospasm?: boolean;
  cpr_active?: boolean;
}

export function sampleCapno(arg: SampleCapnoArgs | number, _etco2?: number, _rr?: number, _intub?: boolean): number {
  // Back-compat with the old positional signature
  const a: SampleCapnoArgs = typeof arg === 'number'
    ? { t_ms: arg, etco2_mmhg: _etco2 ?? 0, rr_bpm: _rr ?? 12, intubated: !!_intub }
    : arg;
  const { t_ms, etco2_mmhg, rr_bpm, intubated, oesophageal = false, bronchospasm = false, cpr_active = false } = a;

  // Oesophageal intubation = no plateau, just baseline wander
  if (oesophageal) return 0.4 * Math.sin(2 * Math.PI * 0.4 * (t_ms / 1000));
  if (!intubated && etco2_mmhg < 5) return 0;

  const period = 60_000 / Math.max(rr_bpm, 4);
  const inCycle = (t_ms % period) / period;
  const top = etco2_mmhg;

  let value: number;
  if (bronchospasm) {
    // Shark-fin: slow upstroke and slow plateau; no flat phase-3.
    // 0-30% inspiration, 30-100% slow rising expiration with no real plateau.
    if (inCycle < 0.30) value = 0;
    else value = top * (Math.pow((inCycle - 0.30) / 0.70, 0.7));
  } else {
    // Normal phase morphology
    if (inCycle < 0.25) value = 0;
    else if (inCycle < 0.35) value = top * ((inCycle - 0.25) / 0.10);     // phase 2 upstroke
    else if (inCycle < 0.85) value = top * (1 + (inCycle - 0.35) * 0.05); // phase 3 alveolar plateau (slight upslope)
    else value = top * Math.max(0, 1 - (inCycle - 0.85) / 0.15);          // phase 0 downstroke
  }

  // CPR — compressions superimpose irregular wavelets on the trace
  if (cpr_active && intubated) {
    value += 1.5 * Math.sin(2 * Math.PI * 1.66 * (t_ms / 1000));
  }
  return Math.max(0, value);
}

// Pleth: smoothed pulse oximeter waveform with amplitude scaled by SpO2 + perfusion.
// During CPR in cardiac arrest the patient has no intrinsic HR (hr=0) but mechanical
// compressions DO generate detectable pulses on the SpO2 probe — pulse at the LIVE
// compression rate the learner is hitting (not a hard-coded target).
//
// `cpr_rate_cpm` is the rolling-window measurement of the learner's tap rate from the
// server-side compressions engine.
export function samplePleth(t_ms: number, hr: number, spo2: number, perfusion_pct: number, cpr_rate_cpm = 0): number {
  if (perfusion_pct < 5) return 0;
  // Effective pulsation rate: intrinsic HR if perfusing, else live compression rate.
  const effectiveRate = hr > 0 ? hr : (cpr_rate_cpm > 20 ? cpr_rate_cpm : 0);
  if (effectiveRate === 0) return 0;
  const period = 60_000 / effectiveRate;
  const phase = (t_ms % period) / period;
  if (hr > 0) {
    // Perfusing — two-bump curve (systolic + dicrotic)
    const a1 = 0.8 * (perfusion_pct / 100);
    const a2 = 0.25 * (perfusion_pct / 100);
    return ( a1 * Math.exp(-Math.pow((phase - 0.18) / 0.10, 2))
           + a2 * Math.exp(-Math.pow((phase - 0.45) / 0.12, 2))
           ) * (spo2 / 100);
  }
  // CPR-only — single rounded bump per compression, no dicrotic notch, lower amplitude
  const amp = 0.5 * (perfusion_pct / 100);
  return amp * Math.exp(-Math.pow((phase - 0.25) / 0.13, 2)) * (spo2 / 100);
}
